import { useQuery } from "@apollo/client";
import {
  IonCard,
  IonCardContent,
  IonContent,
  IonHeader,
  IonIcon,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonItem,
  IonLabel,
  IonList,
  IonListHeader,
  IonPage,
  IonText,
  IonTitle,
  IonToolbar,
  useIonViewWillEnter,
} from "@ionic/react";
import { ListSkeleton } from "components/Skeleton/ListSkeleton";
import { AuthContext } from "contexts/AuthContext";
import { GET_NOTIFICATIONS } from "graphql/queries";
import useAccount from "hooks/useAccount";
import useMerchantProfile from "hooks/useMerchantProfile";
import {
  checkmark,
  checkmarkCircleOutline,
  flashOutline,
  qrCodeOutline,
  shareOutline,
} from "ionicons/icons";
import { useContext, useEffect, useMemo, useState } from "react";
import { AccountStore } from "stores/AccountStore";
import { NotificationStore } from "stores/NotificationStore";
import "./Notifications.css";

const Notifications = () => {
  const { authState } = useContext(AuthContext);
  const [loadAccount, { data: accountData }] = useAccount(authState);
  const { data: keyData, refetch: refetchKeys } = useMerchantProfile(
    authState.user.uid,
    true
  );
  const notifications = NotificationStore.useState((s) => s.notifications);
  const cursor = NotificationStore.useState((s) => s.cursor);
  const [numNewItems, setNumNewItems] = useState(20);
  const [fetchCursor, setFetchCursor] = useState(cursor);
  const { data, loading, refetch } = useQuery(GET_NOTIFICATIONS, {
    variables: { cursor: fetchCursor, limit: 20 },
  });
  const [loaded, setLoaded] = useState(false);

  const _newNotifications = useMemo(() => notifications, [notifications]);

  const newNotifications = useMemo(
    () => _newNotifications.slice().slice(0, numNewItems),
    [_newNotifications, numNewItems]
  );

  const openNotification = (notification) => {
    NotificationStore.update((s) => {
      s.currentNotification = notification;
      s.openModal = true;
    });
  };

  const notificationIcon = (type) => {
    switch (type) {
      case "withdrawal_success":
        return shareOutline;
      case "purchase_success" || "purchase_addons_success":
        return checkmarkCircleOutline;
      case "reward_received":
        return flashOutline;
      case "redeemed_voucher":
        return qrCodeOutline;
      case "payment_received":
        return flashOutline;
      default:
        return flashOutline;
    }
  };

  const notificationColor = (type) => {
    switch (type) {
      case "withdrawal_success":
        return "success";
      case "purchase_success" || "purchase_addons_success":
        return "success";
      case "reward_received":
        return "warning";
      case "redeemed_voucher":
        return "danger";
      case "payment_received":
        return "success";
      default:
        return "primary";
    }
  };

  useEffect(() => {
    if (data?.notifications?.length > 0) {
      setLoaded(true);
      NotificationStore.update((s) => {
        s.notifications = [...s.notifications, ...data.notifications];
        s.cursor =
          data?.notifications[data?.notifications.length - 1].created_at;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useIonViewWillEnter(() => {
    refetch();
    refetchKeys();
    loadAccount();
  });

  useEffect(() => {
    if (accountData?.users_by_pk) {
      AccountStore.update((s) => {
        s.account = { ...accountData.users_by_pk };
      });
    }
  }, [accountData]);

  return (
    <IonPage className={"notification-page-root"}>
      <IonHeader className={"ion-no-border"}>
        <IonToolbar className={"background-color"}>
          <IonTitle>Notifications</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <IonHeader collapse="condense">
          <IonToolbar>
            <IonTitle size="large">Notifications</IonTitle>
          </IonToolbar>
        </IonHeader>
        {
          <IonCard
            className={`${
              !keyData?.merchants ||
              (keyData?.merchants &&
                (!keyData?.merchants[0]?.business_name ||
                  !(keyData?.merchants[0]?.kyc_status === "approved") ||
                  !(
                    keyData?.merchants[0]?.opennode_account_id ||
                    keyData?.merchants[0]?.opennode_key_public
                  )))
                ? ""
                : "ion-hide"
            }`}
          >
            <IonCardContent className="ion-no-padding">
              <IonList lines="inset">
                <IonListHeader>Set up your account</IonListHeader>
                <IonItem routerLink={"/account/business"} detail={false}>
                  <IonLabel className="ion-text-wrap">
                    <IonText color="primary">Business Information</IonText>
                    <p>Tell us and local shoppers about your business.</p>
                  </IonLabel>
                  {keyData?.merchants &&
                    keyData?.merchants[0]?.business_name && (
                      <IonIcon icon={checkmark} color="success" slot="end" />
                    )}
                </IonItem>
                <IonItem
                  routerLink={"/account/lsp"}
                  detail={false}
                  disabled={
                    !(
                      keyData?.merchants && keyData?.merchants[0]?.business_name
                    )
                  }
                >
                  <IonLabel className="ion-text-wrap">
                    <IonText color="primary">Take Your First Payment</IonText>
                    <p>
                      Get connected with a bitcoin service provider; start
                      accepting bitcoin payments right away.
                    </p>
                  </IonLabel>
                  {keyData?.merchants &&
                    keyData?.merchants[0]?.id &&
                    (keyData?.merchants[0]?.provider_keys?.length > 0 ||
                      keyData?.merchants[0]?.opennode_account_id) && (
                      <IonIcon icon={checkmark} color="success" slot="end" />
                    )}
                </IonItem>
                <IonItem
                  routerLink={"/account/square"}
                  detail={false}
                  disabled={!(keyData?.merchants && keyData?.merchants[0]?.id)}
                >
                  <IonLabel className="ion-text-wrap">
                    <IonText color="primary">Connect to Square</IonText>
                    <p>
                      Using Square? This inetegration allows for a seamless
                      experience at your Square point of sale.
                    </p>
                  </IonLabel>
                  {keyData?.merchants &&
                    keyData?.merchants[0]?.id &&
                    keyData?.merchants[0]?.block_tokens.length > 0 && (
                      <IonIcon icon={checkmark} color="success" slot="end" />
                    )}
                </IonItem>
                {keyData?.merchants[0]?.opennode_account_id &&
                  keyData?.merchants[0]?.provider_keys?.filter(
                    (x) => x.default === true
                  ).length === 0 && (
                    <IonItem
                      routerLink={"/account/welcome"}
                      detail={false}
                      lines="none"
                      disabled={
                        !(keyData?.merchants && keyData?.merchants[0]?.id) ||
                        !(
                          keyData?.merchants &&
                          keyData?.merchants[0]?.id &&
                          keyData?.merchants[0]?.opennode_account_id
                        )
                      }
                    >
                      <IonLabel className="ion-text-wrap">
                        <IonText color="primary">Business Verification</IonText>
                        <p>
                          You can accept payments right away, but before you can
                          withdraw your money, you'll need to verify your
                          business.
                        </p>
                      </IonLabel>
                      {keyData?.merchants &&
                        keyData?.merchants[0]?.id &&
                        keyData?.merchants[0]?.kyc_status === "approved" && (
                          <IonIcon
                            icon={checkmark}
                            color="success"
                            slot="end"
                          />
                        )}
                    </IonItem>
                  )}
              </IonList>
            </IonCardContent>
          </IonCard>
        }
        {loading && !loaded && <ListSkeleton rows={20} />}

        <IonList lines="none" className={"wallet-list"}>
          {newNotifications.map((notification, i) => {
            return (
              <IonItem
                button
                detail={false}
                className={"wallet-item"}
                onClick={() => openNotification(notification)}
                key={"notification-" + notification.id + i}
              >
                <IonIcon
                  icon={notificationIcon(notification.type)}
                  slot="start"
                  color={notificationColor(notification.type)}
                />
                <IonLabel className={"ion-text-wrap"}>
                  <h2>{notification.title}</h2>
                  <p>
                    {notification.message}{" "}
                    {new Date(notification.created_at).toDateString()}
                  </p>
                </IonLabel>
              </IonItem>
            );
          })}
        </IonList>
        <IonInfiniteScroll
          onIonInfinite={async (event) => {
            if (numNewItems > _newNotifications.length) {
              return event.target.complete();
            }
            setFetchCursor(cursor);
            await refetch();

            const numItems = numNewItems + 20;
            setNumNewItems(numItems);
            event.target.complete();
          }}
          position={"bottom"}
          threshold={"100px"}
        >
          <IonInfiniteScrollContent
            loadingSpinner={"bubbles"}
          ></IonInfiniteScrollContent>
        </IonInfiniteScroll>
      </IonContent>
    </IonPage>
  );
};

export default Notifications;
